import React from "react";
import { FaShoppingCart, FaGlobe, FaCog } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import SEO from '../components/SEO';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';
import ProofStrip from '../components/page/ProofStrip';
import heroImage from '../assets/ecommerce/hero-market.webp';
import ctaImage from '../assets/ecommerce/cta-open.webp';

/**
 * /ecommerce-coming-soon
 *
 * Rebuilt onto the shared page kit. Off the blue/cyan gradient it shared with
 * the old /booking-websites, and two genuine defects fixed:
 *
 *   1. Both CTAs were bare <button> elements with NO onClick handler — "Get
 *      Notified" and "Learn More" were inert. They are real links now:
 *      the contact form, and the Limelite ecommerce product page.
 *   2. `hover:${primaryTextColor}` interpolated a Tailwind class name, which
 *      the build never generates, so that hover state did not exist.
 *
 * Also removed a useEffect whose timer callback was an empty comment.
 *
 * CONSOLIDATION NOTE: this page is a custom-development lead path. Its Limelite
 * links deliberately point to the single Limelite platform home, not a separate
 * ecommerce product funnel.
 */
const EcommerceComingSoon = () => {
    const { t } = useTranslation();

    const primaryTextColor = "text-silver-light";
    const secondaryTextColor = "text-silver-medium";

    const [contentRef, contentInView] = useInView({
        triggerOnce: true,
        threshold: 0.15,
        rootMargin: "-50px",
    });

    const features = [
        { key: "advancedEcommerce", icon: <FaShoppingCart className="text-2xl" /> },
        { key: "globalReach", icon: <FaGlobe className="text-2xl" /> },
        { key: "smartAutomation", icon: <FaCog className="text-2xl" /> },
    ];

    return (
        <div className="min-h-screen bg-black font-sans">
            <SEO
                title="Ecommerce Website Development | Custom Ecommerce Platform for Small Business"
                description="Ecommerce website developer for small business teams that need custom storefronts, fast checkout, and secure payments. Custom ecommerce website development built for growth and happy customers."
                keywords="ecommerce website development, ecommerce website developer for small business, custom ecommerce platform, hire web developer for ecommerce website, website developer for business, ecommerce developer Croatia"
                url="/ecommerce-coming-soon"
            />

            <PageHero
                eyebrow={t('ecommerceComingSoon.hero.eyebrow', 'Ecommerce')}
                title={t('ecommerceComingSoon.hero.title')}
                subtitle={t('ecommerceComingSoon.hero.subtitle')}
                ctaLabel={t('ecommerceComingSoon.hero.cta', 'Talk to us about your store')}
                ctaTo="/contact"
                secondaryLabel={t('ecommerceComingSoon.hero.secondary', 'See Limelite Ecommerce')}
                secondaryTo="/limelite"
                proofLine={t('ecommerceComingSoon.hero.launch2026')}
                backgroundImage={heroImage}
            />

            <ProofStrip />

            {/* Features */}
            <section ref={contentRef} className="py-20 bg-black">
                <div className="container mx-auto px-4">
                    <SectionHeading
                        eyebrow={t('ecommerceComingSoon.features.eyebrow', 'What it does')}
                        title={t('ecommerceComingSoon.features.title')}
                        lead={t('ecommerceComingSoon.features.subtitle')}
                        className="mb-12"
                    />

                    <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3">
                        {features.map((feature, index) => (
                            <div
                                key={feature.key}
                                className={`flex flex-col rounded-lg border border-gray-800 bg-off-black p-8 transition-all duration-1000 transform hover:border-moss/50 ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-moss/50 bg-moss-deep/30 text-moss-light">
                                    {feature.icon}
                                </div>
                                <h3 className={`mb-3 text-xl font-medium ${primaryTextColor}`}>
                                    {t(`ecommerceComingSoon.features.${feature.key}.title`)}
                                </h3>
                                <p className={`${secondaryTextColor} flex-grow text-sm leading-relaxed`}>
                                    {t(`ecommerceComingSoon.features.${feature.key}.description`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Closing CTA. Both of these were <button> with no handler. */}
            <section className="relative overflow-hidden">
                <img
                    src={ctaImage}
                    alt=""
                    aria-hidden="true"
                    className="h-[26rem] w-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-4 text-center">
                        <div className="mx-auto max-w-3xl">
                            <h2 className={`mb-4 text-3xl md:text-4xl font-serif font-medium ${primaryTextColor}`}>
                                {t('ecommerceComingSoon.cta.title')}
                            </h2>
                            <p className={`${secondaryTextColor} mb-8`}>{t('ecommerceComingSoon.cta.subtitle')}</p>
                            <div className="flex flex-col items-center justify-center gap-5 sm:flex-row">
                                <Link
                                    to="/contact"
                                    className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
                                >
                                    {t('ecommerceComingSoon.cta.getNotified')}
                                </Link>
                                <Link
                                    to="/limelite"
                                    className="group relative inline-block py-3 text-lg font-medium text-silver-light transition-colors duration-300 hover:text-white"
                                >
                                    {t('ecommerceComingSoon.cta.learnMore')}
                                    <span className="absolute bottom-1 left-0 block h-px w-full origin-left scale-x-0 bg-moss transition-transform duration-300 ease-out group-hover:scale-x-100" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EcommerceComingSoon;
